import { useState } from 'react';
import { T, useGT } from 'gt-react';
import { useNavigate } from '@tanstack/react-router';
import {
  ClipboardTextIcon,
  DotsThreeVerticalIcon,
  ListChecksIcon,
  PencilSimpleIcon,
  PlusIcon,
  TableIcon,
  TrashIcon,
} from '@phosphor-icons/react';
import type { ItemListDTO } from '@erp/rpc';
import Page from 'src/components/page/Page';
import PaginatedGentlyTable, {
  type TableColumn,
} from '@/components/common/PaginatedGentlyTable';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/context/ToastContext';
import {
  useDeleteItemList,
  useItemLists,
} from '@/api/rpc/hooks/inventory-management/useItemLists';
import { useUsersInOrg } from '@/api/rpc/hooks/core/useUsersInOrg';
import { NewItemListDialog } from './NewItemListDialog';
import { EditItemListDialog } from './EditItemListDialog';
import { CreateReviewFromListDialog } from './CreateReviewFromListDialog';

function formatDate(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleDateString();
}

// ─── Row actions ──────────────────────────────────────────────────────────────

// What a row's ⋮ menu can start; the page owns the single open dialog.
type ListAction = 'createReview' | 'edit' | 'delete';

function ItemListRowActions({
  list,
  onOpenInCatalog,
  onAction,
}: {
  list: ItemListDTO;
  onOpenInCatalog: () => void;
  onAction: (action: ListAction) => void;
}) {
  const gt = useGT();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 w-7 p-0"
          aria-label={gt('Actions for {name}', {
            context: 'aria label for an item list row actions menu',
            name: list.name,
          })}
        >
          <DotsThreeVerticalIcon className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={onOpenInCatalog}>
          <TableIcon className="h-4 w-4" />
          <T context="item list row action: open the catalog filtered to this list">
            Open in catalog
          </T>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => onAction('createReview')}>
          <ClipboardTextIcon className="h-4 w-4" />
          <T context="item list row action: stage the list as a new review">
            Create review
          </T>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => onAction('edit')}>
          <PencilSimpleIcon className="h-4 w-4" />
          <T context="item list row action: rename the list or edit its description">
            Rename
          </T>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          className="text-red-600 focus:text-red-700"
          onClick={() => onAction('delete')}
        >
          <TrashIcon className="h-4 w-4" />
          <T context="item list row action: delete the list">Delete</T>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// ─── Delete confirmation ──────────────────────────────────────────────────────

function DeleteItemListDialog({
  list,
  onOpenChange,
}: {
  list: ItemListDTO;
  onOpenChange: (open: boolean) => void;
}) {
  const gt = useGT();
  const { showToast } = useToast();
  const deleteList = useDeleteItemList();

  const confirm = () => {
    deleteList.mutate(
      { id: list.id },
      {
        onSuccess: () => {
          showToast({
            title: gt('Item list deleted', {
              context: 'toast title after deleting an item list',
            }),
            message: gt('"{name}" was deleted for the whole organization.', {
              context: 'toast message after deleting an item list',
              name: list.name,
            }),
            color: 'success',
          });
          onOpenChange(false);
        },
        onError: (error) =>
          showToast({
            title: gt('Error', {
              context: 'delete item list error toast title',
            }),
            message:
              error.message ||
              gt('Failed to delete the item list.', {
                context: 'fallback error when deleting an item list fails',
              }),
            color: 'error',
          }),
      },
    );
  };

  return (
    <AlertDialog open onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {gt('Delete "{name}"?', {
              context: 'delete item list confirmation title',
              name: list.name,
            })}
          </AlertDialogTitle>
          <AlertDialogDescription>
            <T context="delete item list confirmation body">
              Item lists are shared with your whole organization — deleting this
              one removes it for everyone, and any catalog view filtered by it
              will be cleared.
            </T>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={deleteList.isPending}>
            <T context="cancel button">Cancel</T>
          </AlertDialogCancel>
          <AlertDialogAction
            className="bg-red-600 hover:bg-red-700"
            disabled={deleteList.isPending}
            onClick={(event) => {
              event.preventDefault();
              confirm();
            }}
          >
            <T context="delete confirm button">Delete</T>
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

/**
 * The org's shared item lists: durable, reusable sets of catalog products.
 * Each row opens the catalog scoped to the list, stages the list as a new
 * review, or manages the list itself; the header's "New list" builds one from
 * pasted item numbers. Saving a catalog selection as a list lives on the
 * catalog page's staging actions.
 */
export function ItemListsPage() {
  const gt = useGT();
  const navigate = useNavigate();
  const { lists, isLoading, error } = useItemLists();
  // Creators are looked up from the compact org-user listing — the list DTO
  // only carries the id.
  const { data: users } = useUsersInOrg();

  const [newListOpen, setNewListOpen] = useState(false);
  // The single open row dialog: which list, and which action.
  const [activeDialog, setActiveDialog] = useState<{
    list: ItemListDTO;
    action: ListAction;
  } | null>(null);

  const userNameById = new Map(
    (users ?? []).map((user) => [user.id, user.name]),
  );

  const openInCatalog = (list: ItemListDTO) =>
    void navigate({
      to: '/item-master/catalog',
      search: { itemListId: list.id },
    });

  const closeDialog = (open: boolean) => {
    if (!open) setActiveDialog(null);
  };

  const columns: TableColumn<ItemListDTO>[] = [
    {
      id: 'name',
      label: gt('Name', { context: 'item lists table column header' }),
      render: (_value: unknown, row: ItemListDTO) => (
        <div className="min-w-0">
          <div className="font-medium">{row.name}</div>
          {row.description && (
            <div className="truncate text-xs text-muted-foreground">
              {row.description}
            </div>
          )}
        </div>
      ),
    },
    {
      id: 'itemCount',
      label: gt('Items', {
        context: 'item lists table column header, product count',
      }),
      render: (value: unknown) =>
        typeof value === 'number' ? value.toLocaleString() : '—',
    },
    {
      id: 'createdByUserId',
      label: gt('Creator', { context: 'item lists table column header' }),
      render: (value: unknown) =>
        (typeof value === 'string' && userNameById.get(value)) || '—',
    },
    {
      id: 'updatedAt',
      label: gt('Updated', { context: 'item lists table column header' }),
      render: (value: unknown) =>
        typeof value === 'string' ? formatDate(value) : '—',
    },
    {
      // The row-actions column renders off the id key (TableColumn ids must
      // be keys of the row type).
      id: 'id',
      label: '',
      align: 'right',
      stopRowClick: true,
      render: (_value: unknown, row: ItemListDTO) => (
        <ItemListRowActions
          list={row}
          onOpenInCatalog={() => openInCatalog(row)}
          onAction={(action) => setActiveDialog({ list: row, action })}
        />
      ),
    },
  ];

  return (
    <Page
      title={<T context="page title, item master lists">Lists</T>}
      subtitle={
        <T context="subtitle for the item lists page">
          Reusable sets of catalog products, shared with your organization.
        </T>
      }
      isLoading={isLoading && lists.length === 0}
      error={error ?? undefined}
      actions={
        <Button size="sm" onClick={() => setNewListOpen(true)}>
          <PlusIcon className="h-4 w-4" />
          <T context="button opening the new item list dialog">New list</T>
        </Button>
      }
    >
      <div className="mt-6 space-y-6">
        {lists.length === 0 && !isLoading ? (
          <div className="flex flex-col items-center gap-2 py-16 text-muted-foreground">
            <ListChecksIcon className="h-8 w-8" />
            <p className="text-sm font-medium">
              <T context="empty state heading, no item lists">No item lists</T>
            </p>
            <p className="max-w-md text-center text-xs">
              <T context="empty state subtext, no item lists">
                Paste item numbers here with New list, or save a selection from
                the Item Catalog with "Save as list".
              </T>
            </p>
          </div>
        ) : (
          <PaginatedGentlyTable<ItemListDTO>
            data={isLoading ? undefined : lists}
            columns={columns}
            isLoading={isLoading}
            showSearch={false}
            showDownload={false}
            emptyMessage={gt('No item lists.', {
              context: 'empty state message on the item lists table',
            })}
            getRowKey={(row) => row.id}
          />
        )}
      </div>

      {newListOpen && (
        <NewItemListDialog
          open
          onOpenChange={(open) => {
            if (!open) setNewListOpen(false);
          }}
        />
      )}

      {activeDialog?.action === 'createReview' && (
        <CreateReviewFromListDialog
          open
          onOpenChange={closeDialog}
          list={activeDialog.list}
        />
      )}

      {activeDialog?.action === 'edit' && (
        <EditItemListDialog
          open
          onOpenChange={closeDialog}
          list={activeDialog.list}
        />
      )}

      {activeDialog?.action === 'delete' && (
        <DeleteItemListDialog
          list={activeDialog.list}
          onOpenChange={closeDialog}
        />
      )}
    </Page>
  );
}
